import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  SquareArrowOutUpRight,
  FileText,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
} from "lucide-react";

import PageHeader from "../paragraphHeader";
import NotFoundImage from "../NotFound";
import { galleryFetch } from "../../services/api/Gallery";

interface PdfFile {
  name: string;
  url: string;
}

interface GalleryItem {
  id: number;
  category_name: string | null;
  title: string | null;
  slug: string | null;
  image: string | null;
  pdf: PdfFile[] | null;
}

export function SocialMediaPage() {
  const { slug } = useParams<{ slug: string }>();

  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // ================= STATIC SOCIAL LINKS =================
  const socialLinks = [
    {
      label: "Instagram",
      url: "https://www.instagram.com/haryanaolympicassociation",
      icon: Instagram,
    },
    {
      label: "Facebook",
      url: "https://www.facebook.com/p/Haryana-Olympic-Association-61574667794025/",
      icon: Facebook,
    },
    {
      label: "LinkedIn",
      url: "https://in.linkedin.com/company/haryanaolympicassociation",
      icon: Linkedin,
    },
    {
      label: "YouTube",
      url: "https://www.youtube.com/@HaryanaOlympicAssociation",
      icon: Youtube,
    },
    {
      label: "Twitter / X",
      url: "https://x.com/Haryana_olympic",
      icon: null,
    },
  ];

  useEffect(() => {
    fetchGallery();
  }, [slug]);

  const fetchGallery = async () => {
    try {
      setLoading(true);

      const endpoint = slug ? `social-galleries/${slug}` : "media";
      const response = await galleryFetch(endpoint);

      if (response?.success) {
        setGallery(response.data?.data?.[0]?.children || []);
      } else {
        setGallery([]);
      }
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
      setGallery([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className=" w-full mt-10">
      <PageHeader
        title={
          <h1 className="font-semibold text-[#1F4D8F] uppercase">
            {slug?.replace(/-/g, " ") || "Sports Media"}
          </h1>
        }
        subtitle={
          <>
            Explore{" "}
            <span className="text-blue-500 font-bold">Sports Media</span>
          </>
        }
        paragraph="Explore Haryana's premier sports events and championships."
        backgroundImage=""
        align="center"
        showOlympicsIcon={true}
      />

      <div className="max-w-2xl mx-auto rounded-2xl p-4">
        {/* ================= SOCIAL MEDIA ================= */}
        <div className="mb-2">
          <h2 className="text-3xl font-bold text-slate-800 capitalize">
            Follow Us
          </h2>

          <div className="w-20 h-1 bg-yellow-400 rounded-full mt-3" />

          <div className="flex items-center gap-4 mt-6">
            {socialLinks.map(({ label, url, icon: Icon }) => {
              const styles: Record<string, string> = {
                Instagram:
                  "bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600",
                Facebook: "bg-[#1877F2]",
                LinkedIn: "bg-[#0A66C2]",
                YouTube: "bg-[#FF0000]",
                "Twitter / X": "bg-black",
              };

              return (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className={`
            w-20 h-20
            rounded-lg
            flex items-center justify-center
            text-white
            ${styles[label]}
            transition-all duration-300
            hover:scale-110
            hover:shadow-xl
          `}
                >
                  {Icon ? (
                    <Icon className="w-9 h-9" strokeWidth={2.5} />
                  ) : (
                    <span className="text-3xl font-bold leading-none">𝕏</span>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
